import React, { useState, useEffect } from "react";
import {
  CloseIcon,
  IconAttach,
  IconCommissioner,
  IconEdit,
  IconFacebook,
  IconInstagram,
  IconTiktok,
  IconX,
  IconYoutube,
} from "../components/CustomIcons";
import { useAuth } from "../contexts/AuthContext";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  getLeagueBySlug,
  updateLeagueProgressStep,
} from "../components/leagueLocker/firestoreQuery";
import defaultTeamImage from "../assets/images/defaultTeamPic.svg";
import { encryptObject } from "../components/utils/crypto";
import { LeagueContext } from "./LeagueLockerPage";
import { SectionLoader } from "../components/ui/SectionLoader";

const LeagueLockerTeamsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<any>(null);
  const [leagueData, setLeagueData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [inviteButtonText, setInviteButtonText] = useState<string | null>(
    "Invite"
  );
  const [teams, setTeams] = useState<any[]>([]);
  const { leagueUrl } = useOutletContext<LeagueContext>();

  const fetchLeagueDetails = async () => {
    try {
      setIsLoading(true);
      const res = await getLeagueBySlug(leagueUrl || "");
      setLeagueData(res);
      setTeams(res?.teams ?? []);
    } catch (error) {
      console.error("Error fetching league:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeagueDetails();
  }, [leagueUrl]);

  const handleEditClick = () => {
    navigate(`/manage-league/${leagueData.leagueUrl}/edit-my-team`, {
      state: { fromTeamsPage: true },
    });
  };

  const handleModalOpen = (team: any) => {
    setShowModal(true);
    setSelectedTeam(team);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedTeam(null);
  };

  const handleInvite = async () => {
    const url = window.location.href;

    const payload = { isEncoded: true, slug: leagueUrl };
    const token = encryptObject(payload);
    const inviteLink = `${window.location.origin}/${token}`;

    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteButtonText("Link Copied");
      await updateLeagueProgressStep(leagueData.id, "invite-other-teams");
      setTimeout(() => {
        setInviteButtonText("Invite");
      }, 3000);
    } catch (err) {
      // fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const checkEligibility = () => {
    if (leagueData?.teams?.length === +leagueData?.leagueSize) {
      return false;
    }
    return true;
  };

  if (isLoading) {
    return <SectionLoader />;
  }

  return (
    <>
      <div className="yourLeagueHeader">
        <div className="headingItem">Teams</div>
        {checkEligibility() && leagueData.createdBy === user?.uid && (
          <div className="actionItems" onClick={handleInvite}>
            <button type="button" className="btnStyle">
              {inviteButtonText}
              <IconAttach />
            </button>
          </div>
        )}
      </div>
      <ul className="teamsList">
        {teams.length > 0 &&
          teams.map((team: any) => (
            <li onClick={() => handleModalOpen(team)} key={team.createdBy}>
              <div className="teamsPic">
                <img src={team.imageUrl || defaultTeamImage} alt="" />
              </div>
              <div className="teamsDesc">
                {leagueData.createdBy === team.createdBy && (
                  <div className="badgeCommissioner">
                    <IconCommissioner /> Commissioner
                  </div>
                )}
                {team.createdBy === user?.uid && (
                  <button
                    className="editTeamBtn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick();
                    }}
                  >
                    <IconEdit />
                  </button>
                )}
                <div className="blockHeading">
                  <span>{team.name}</span>
                </div>
              </div>
            </li>
          ))}
      </ul>
      {showModal && selectedTeam && (
        <div className="detailPopupWrap">
          <div className="detailPopupInner">
            <div className="detailPicWrap">
              <img src={selectedTeam?.imageUrl || defaultTeamImage} alt="" />
              <div className="detailPicCont">{selectedTeam?.name}</div>
              <div className="closePopup" onClick={handleModalClose}>
                <CloseIcon />
              </div>
            </div>
            <div className="detailPopupContent">
              <div className="detailContentHeading">
                <span>{selectedTeam?.name}</span>
                <span>{selectedTeam?.homeTown}</span>
              </div>
              <div className="prose detailContent">
                <Markdown remarkPlugins={[remarkGfm]}>
                  {selectedTeam?.description}
                </Markdown>
              </div>

              {(selectedTeam?.facebook ||
                selectedTeam?.instagram ||
                selectedTeam?.x ||
                selectedTeam?.youtube ||
                selectedTeam?.tiktok) && (
                <div className="followBlk">
                  <span>Follow Me</span>
                  <div className="followOptions">
                    {selectedTeam?.tiktok && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(selectedTeam?.tiktok, "_blank");
                        }}
                      >
                        <IconTiktok />
                      </span>
                    )}
                    {selectedTeam?.facebook && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(selectedTeam?.facebook, "_blank");
                        }}
                      >
                        <IconFacebook />
                      </span>
                    )}
                    {selectedTeam?.instagram && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(selectedTeam?.instagram, "_blank");
                        }}
                      >
                        <IconInstagram />
                      </span>
                    )}
                    {selectedTeam?.x && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(selectedTeam?.x, "_blank");
                        }}
                      >
                        <IconX />
                      </span>
                    )}
                    {selectedTeam?.youtube && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(selectedTeam?.youtube, "_blank");
                        }}
                      >
                        <IconYoutube />
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeagueLockerTeamsPage;
